import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Boxes, Activity, HandCoins, Package, Store, LogOut, TrendingUp,
  Users, ShieldCheck, Eye, ScrollText, FileText, Building2, Settings as SettingsIcon,
  ClipboardEdit, FileDown, Menu, X, Receipt,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Toaster } from "@/components/ui/sonner";
import NotificationBell from "@/components/NotificationBell";
import { API, api } from "@/lib/api";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/input-harian", label: "Input Data Harian", icon: ClipboardEdit, testid: "nav-input-harian" },
  { to: "/riwayat", label: "Riwayat Transaksi", icon: ScrollText, testid: "nav-riwayat" },
  { to: "/tagihan", label: "Tagihan", icon: Receipt, testid: "nav-tagihan", badgeKey: "tagihan" },
  { to: "/laporan", label: "Laporan PDF", icon: FileDown, testid: "nav-laporan", allowedRoles: ["super_admin", "admin", "cabang"] },
  { to: "/cabang", label: "Lokasi Cabang", icon: Building2, testid: "nav-cabang", allowedRoles: ["super_admin", "admin"] },
  { to: "/pengguna", label: "Pengguna", icon: Users, testid: "nav-pengguna", allowedRoles: ["super_admin", "admin"] },
  { to: "/audit", label: "Catatan Audit", icon: FileText, testid: "nav-audit", allowedRoles: ["super_admin", "admin", "cabang"] },
  { to: "/pengaturan", label: "Pengaturan", icon: SettingsIcon, testid: "nav-pengaturan" },
];

const roleLabel = { super_admin: "Super Admin", admin: "Usaha", cabang: "Cabang", viewer: "Viewer" };
const roleTone = {
  super_admin: "bg-amber-500/15 text-amber-300",
  admin: "bg-emerald-500/15 text-emerald-300",
  cabang: "bg-slate-700 text-slate-300",
  viewer: "bg-sky-500/15 text-sky-300",
};

export default function Layout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [badges, setBadges] = useState({ tagihan: 0 });

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchBadges = async () => {
      try {
        const { data } = await api.get("/tagihan");
        if (!cancelled) setBadges({ tagihan: data?.count || 0 });
      } catch { /* ignore */ }
    };
    fetchBadges();
    const iv = setInterval(fetchBadges, 60_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user]);

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500">Memuat...</div>;
  }

  const visible = nav.filter((n) => !n.allowedRoles || n.allowedRoles.includes(user.role));

  const SidebarContent = ({ onLinkClick }) => (
    <>
      <div className="px-6 py-6 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-emerald-500 flex items-center justify-center overflow-hidden">
            {user.workspace?.logo_file_id ? (
              <img src={`${API}/files/${user.workspace.logo_file_id}?token=${encodeURIComponent(localStorage.getItem("session_token") || "")}`}
                   alt="Logo" className="h-9 w-9 object-cover" data-testid="sidebar-logo-img" />
            ) : (
              <Store className="h-5 w-5 text-white" />
            )}
          </div>
          <div>
            <div className="font-heading text-base font-bold tracking-tight" data-testid="sidebar-workspace-name">
              {user.workspace?.name || "Kelola Usaha"}
            </div>
            <div className="text-[11px] text-slate-400">
              {user.role === "cabang" && user.cabang ? user.cabang.name : "Manajemen Keuangan"}
            </div>
          </div>
        </div>
      </div>

      <nav className="flex-1 py-4 app-scroll overflow-y-auto">
        {visible.map((n) => {
          const count = n.badgeKey ? (badges[n.badgeKey] || 0) : 0;
          return (
            <NavLink key={n.to} to={n.to} data-testid={n.testid} onClick={onLinkClick}
                     className={({ isActive }) => `flex items-center gap-3 px-6 py-3 text-sm font-medium transition-all ${
                       isActive ? "sidebar-link-active" : "text-slate-300 hover:bg-white/5 hover:text-white"
                     }`}>
              <n.icon className="h-4 w-4" />
              <span className="flex-1">{n.label}</span>
              {count > 0 && (
                <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-bold"
                      data-testid={`nav-badge-${n.badgeKey}`}>
                  {count}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="p-4 border-t border-slate-800 space-y-3">
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-emerald-600 text-white text-xs">
              {(user.name || user.email).slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate" data-testid="user-name">{user.name || user.email}</div>
            <div className="text-[11px] text-slate-400 truncate">{user.email}</div>
          </div>
          <NotificationBell />
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-1 ${roleTone[user.role]}`}
                data-testid="user-role-badge">
            {user.role === "super_admin" ? <ShieldCheck className="h-3 w-3" /> :
             user.role === "admin" ? <ShieldCheck className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
            {roleLabel[user.role] || user.role}
          </span>
          <button data-testid="logout-btn" onClick={logout}
                  className="flex-1 flex items-center gap-2 justify-center rounded-lg border border-slate-700 hover:bg-slate-800 py-2 text-sm text-slate-200 transition-colors">
            <LogOut className="h-4 w-4" /> Keluar
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar Desktop */}
      <aside className="hidden md:flex w-64 shrink-0 bg-slate-900 text-slate-100 flex-col">
        <SidebarContent />
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-slate-900 text-white flex items-center justify-between px-4 py-3 shadow-lg">
        <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"
                className="p-1.5 rounded hover:bg-white/10">
          <Menu className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded bg-emerald-500 flex items-center justify-center overflow-hidden">
            {user.workspace?.logo_file_id ? (
              <img src={`${API}/files/${user.workspace.logo_file_id}?token=${encodeURIComponent(localStorage.getItem("session_token") || "")}`}
                   alt="Logo" className="h-7 w-7 object-cover" />
            ) : <Store className="h-4 w-4 text-white" />}
          </div>
          <div className="text-sm font-bold truncate max-w-[180px]">{user.workspace?.name || "Kelola Usaha"}</div>
        </div>
        <NotificationBell />
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div className="md:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMobileOpen(false)} data-testid="mobile-overlay" />
          <aside className="md:hidden fixed inset-y-0 left-0 z-50 w-72 bg-slate-900 text-slate-100 flex flex-col shadow-2xl animate-in slide-in-from-left">
            <button onClick={() => setMobileOpen(false)} data-testid="mobile-close-btn"
                    className="absolute top-3 right-3 p-1.5 rounded hover:bg-white/10 text-white z-10">
              <X className="h-5 w-5" />
            </button>
            <SidebarContent onLinkClick={() => setMobileOpen(false)} />
          </aside>
        </>
      )}

      <main className="flex-1 min-w-0 app-scroll overflow-y-auto pt-16 md:pt-0">
        <div className="px-4 md:px-8 py-6 md:py-8 max-w-[1400px]">
          <Outlet />
        </div>
      </main>

      <Toaster richColors position="top-right" />
    </div>
  );
}
